import { useAtomValue } from "jotai";
import { useCallback, useEffect, useMemo } from "react";
import { getElement } from "shared/datamodel/canvas-element";
import { CanvasElement } from "shared/datamodel/schemas";
import { syncServiceAtom } from "state-atoms";
import { useIntegrationsProvider } from "./use-integrations-provider";
import { useDeepEqualMemo } from "./use-deep-memoize";
import { useSubscribe } from "replicache-react";
import { getDerivedGroupId } from "utils/node-utils";
import Konva from "konva";
import { Reflect } from "@workcanvas/reflect/client";
import { M } from "shared/datamodel/mutators";
import { mapColumnValueToText } from "shared/integrations/mapper";
import { useMondayItemsInBulk } from "frontend/canvas-designer-new/integrations/hooks/use-monday-items-bulk";

interface DerivedElementData {
  id: string;
  keyPath: string;
}

interface DerivedIntegrationData {
  elementId: string;
  integrationId: string;
  itemId: string;
  columnId: string;
}

type DerivedData = DerivedElementData | DerivedIntegrationData;

// This function is only used in text block elements to detect derived data
// and return ptoperties to replace. It is currently very generic even though we
// only support changing their  text.
// TODO: Rewrite this function into a simpler text-replace function that fits our use case.
export function useDerivedData(element: CanvasElement | null | undefined, isDataLayerEnabled: boolean) {
  let reflect = useAtomValue(syncServiceAtom)?.getReplicache() as Reflect<M> | null;

  const dummyNode = { attrs: { element } } as Konva.Node;
  const isElementDerived = !!getDerivedGroupId(dummyNode); // Requires konva node to be passed in

  // This disabled processing of derived data for non-derived elements
  // Or when reflect is not available
  if (!reflect || !isElementDerived) {
    reflect = null;
    element = null;
  }

  const extractDerivedValue = useCallback((value: string): DerivedData | null => {
    // check regex for ${derivedElementId:derivedElementPropKeyPath}
    const regex = /\${(.*?):(.*?)}/g;
    const matches = value.matchAll(regex);
    for (const match of matches) {
      const derivedElementId = match[1];
      const derivedElementPropertyKeyPath = match[2];
      const [key, integrationId, itemId, columnId] = derivedElementPropertyKeyPath.split(":");
      if (key === "integration") {
        return { elementId: derivedElementId, integrationId, itemId, columnId };
      }
      // TODO: support formatted text like formula,
      // now we stop on the first match
      return { id: derivedElementId, keyPath: derivedElementPropertyKeyPath };
    }
    return null;
  }, []);

  const derivedElements = useMemo(() => {
    if (!element) return {};
    const propertyToDerived: Record<string, DerivedData> = {};
    for (const [key, value] of Object.entries(element)) {
      if (typeof value !== "string") continue;
      const derivedValue = extractDerivedValue(value);
      if (derivedValue) {
        propertyToDerived[key] = derivedValue;
      }
    }
    return propertyToDerived;
  }, [element, extractDerivedValue]);

  const keys = Object.keys(derivedElements);

  const getKeyPathValue = (element: any, keyPath: string) => {
    const keyPathParts = keyPath.split(".");
    let value = element;
    for (const part of keyPathParts) {
      value = value[part];
      if (value === undefined) return null;
    }
    return value;
  };

  const values = useSubscribe(
    reflect,
    async (tx) => {
      const values: Record<string, any> = {};
      for (const [key, derivedElement] of Object.entries(derivedElements)) {
        if (!("keyPath" in derivedElement)) continue;
        // eslint-disable-next-line no-await-in-loop
        const element = await getElement(tx, derivedElement.id);
        if (!element) continue;
        const value = getKeyPathValue(element, derivedElement.keyPath);
        if (value) {
          values[key] = value;
        }
      }
      return values;
    },
    {},
    [derivedElements]
  );

  const integrations = useSubscribe(
    reflect,
    async (tx) => {
      const integrations: Record<string, { integrationId: string; itemId: string; columnId: string }> = {};
      for (const [key, value] of Object.entries(derivedElements)) {
        if (!("integrationId" in value)) continue;
        // eslint-disable-next-line no-await-in-loop
        const element = await getElement(tx, value.elementId);
        if (!element) continue;
        const elementIntegrationItemIds = Object.keys(element.dataLayer?.integrations?.[value.integrationId] ?? {});
        const itemId = elementIntegrationItemIds.includes(value.itemId) ? value.itemId : elementIntegrationItemIds[0];
        if (!itemId) continue;
        integrations[key] = { integrationId: value.integrationId, itemId, columnId: value.columnId };
      }
      return integrations;
    },
    {},
    [derivedElements]
  );

  // If the data layer is disabled, we return empty strings for the values
  // in order to hide the template string.
  if (!isDataLayerEnabled) {
    const newValues = keys.reduce((acc, key) => {
      acc[key] = "";
      return acc;
    }, {} as Record<string, string>);
    return { keys, values: newValues, integrations: {} };
  }

  return { keys, values, integrations };
}

export function DerivedIntegrationDataHandler({
  integrations,
  onChangeValue,
}: {
  integrations: Record<string, { itemId: string; integrationId: string; columnId: string }>;
  onChangeValue: (values: Record<string, string>) => void;
}) {
  const { getBoardIntegrationConfig } = useIntegrationsProvider();
  const memoedIntegrations = useDeepEqualMemo(integrations);

  // Extract unique item IDs for bulk fetching
  const itemIds = useMemo(() => {
    const uniqueItemIds = new Set<string>();
    for (const integration of Object.values(memoedIntegrations)) {
      uniqueItemIds.add(integration.itemId);
    }
    return [...uniqueItemIds];
  }, [memoedIntegrations]);

  const { data, isLoading } = useMondayItemsInBulk(itemIds);

  // Extract and map column values to text
  const values = useMemo(() => {
    if (!data || isLoading) return {};

    const extractedValues: Record<string, string> = {};

    for (const [key, { integrationId, itemId, columnId }] of Object.entries(memoedIntegrations)) {
      const item = data[itemId];
      if (!item?.data) {
        extractedValues[key] = "";
        continue;
      }

      // Handle "name" column
      if (columnId === "name") {
        extractedValues[key] = item.data.name || "";
        continue;
      }

      // Find and map column value
      const columnValue = item.data.columnValues?.find((col: { id: string }) => col.id === columnId);
      if (!columnValue) {
        extractedValues[key] = "";
        continue;
      }

      // Get column definition from integration config for proper mapping
      const config = getBoardIntegrationConfig(integrationId);
      const column = config?.columns?.find((col: { id: string }) => col.id === columnId);
      const mappedValue = mapColumnValueToText(columnValue.value, column || columnValue);
      extractedValues[key] = mappedValue ? String(mappedValue) : "";
    }

    return extractedValues;
  }, [data, isLoading, memoedIntegrations, getBoardIntegrationConfig]);

  useEffect(() => {
    onChangeValue(values);
  }, [values, onChangeValue]);

  return null;
}
